import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";

import {
    type ISchedule,
    type ModalType,
    defaultSchedule,
} from "Pages/Setup/Scheduler/Scheduler";
import { validNameRegex } from "Pages/Setup/Scheduler/Components/AddSchedule/AddSchedule";
import useAlertContext from "Hooks/useAlertContext";
import useFetch from "Hooks/useFetch";
import CronSelector from "../AddSchedule/CronSelector/CronSelector";
import { convertCronUserTzToMountain as convertCronUserTimezoneToMountain } from "Pages/Setup/Scheduler/Components/AddSchedule/AddSchedule";
import Draggable from "react-draggable";

interface IProps {
    activeModal: ModalType;
    getSchedules: () => void;
    integrations: IIntegration[];
    onSetActiveModal: (modal: ModalType) => void;
    onSetScheduleClicked: (schedule: ISchedule) => void;
    scheduleClicked: ISchedule;
    schedules: ISchedule[];
}

export default function EditSchedule({
    activeModal,
    getSchedules,
    integrations,
    onSetActiveModal,
    onSetScheduleClicked,
    scheduleClicked,
    schedules,
}: IProps) {
    /** Used to control how many integration inputs there are, and adding/removing them */
    const [integrationInputs, setIntegrationInputs] = useState<number>(1);
    const [nameValid, setNameValid] = useState<boolean | undefined>();
    const [isRateInvalid, setIsRateInvalid] = useState<boolean>(false);
    const [editError, setEditError] = useState<string>("");

    const { put } = useFetch();
    const { newAlert } = useAlertContext();

    /** Tracks whether we are editing one schedule that represents multiple schedules on AWS EventBridge
     * due to converting from older Windows Task Scheduler system to newer AWS EventBridge system. If multiple,
     * the edit request goes to its own endpoint to handle deleting the extra schedules and creating a new one.
     */
    const multipleSchedules = useRef<boolean | null>(null);

    // Catch a true value of scheduleClicked.trigger.includes(";") (indicating multiple triggers/schedules)
    // after state update in parent completes. Only set the ref if it is in initial null state to prevent setting it after
    // child CronSelector changes the scheduleClicked state.
    useEffect(() => {
        if (
            multipleSchedules.current === null &&
            scheduleClicked.trigger !== ""
        ) {
            multipleSchedules.current = scheduleClicked.trigger.includes(";");
        }
    }, [scheduleClicked.trigger]);

    // Set correct number of integration inputs. Default state value doesn't work due to async state updates
    useEffect(() => {
        setIntegrationInputs(scheduleClicked.integrations.length);
    }, [scheduleClicked.integrations.length]);

    const handleSubmit = () => {
        if (
            scheduleClicked.integrations.some((i) => i === "Select Integration")
        ) {
            setEditError(
                "Please select an integration from the menu for each integration."
            );
            return;
        }
        setEditError("");

        if (scheduleClicked.trigger.startsWith("rate")) {
            if (nameHasNotChanged()) {
                editRateSchedule();
            } else {
                editRateScheduleWithRename();
            }
        } else {
            // Cron schedule expression
            if (nameHasNotChanged()) {
                editCronSchedule();
            } else {
                editCronScheduleWithRename();
            }
        }
    };

    function nameHasNotChanged() {
        return (
            scheduleClicked.name.trim() === scheduleClicked.originalName?.trim()
        );
    }

    const editRateSchedule = async () => {
        const url = "/api/schedule";

        const response = await put(url, scheduleClicked);

        let data: ISchedule | Response;
        try {
            data = await response.json();
        } catch {
            data = response;
        }

        console.log(data);

        if (response.ok) {
            newAlert({
                message: "Schedule edited successfully",
                color: "success",
            });
            getSchedules();
        } else {
            newAlert({
                message: "Error editing schedule",
                color: "danger",
            });
        }
        onSetActiveModal("");
    };

    const editRateScheduleWithRename = async () => {
        const schedule = { ...scheduleClicked };
        const nameWithUnderscores = schedule.name.trim().replace(/\s+/g, "_");
        schedule.name = nameWithUnderscores;

        const url = "/api/schedulewithrename";

        const response = await put(url, schedule);

        let data: ISchedule | Response;
        try {
            data = await response.json();
        } catch {
            data = response;
        }

        console.log(data);

        if (response.ok) {
            newAlert({
                message: "Schedule edited successfully",
                color: "success",
            });
            getSchedules();
        } else {
            newAlert({
                message: "Error editing schedule",
                color: "danger",
            });
        }
        onSetActiveModal("");
    };

    const editCronSchedule = async () => {
        const triggerInMountainTimezone = convertCronUserTimezoneToMountain(
            scheduleClicked.trigger
        );
        const schedule: ISchedule = {
            ...scheduleClicked,
            trigger: triggerInMountainTimezone,
        };

        // If the schedule is a multiple schedule, send it to the multiple schedules endpoint
        // to delete all grouped schedules and replace with one new one
        const url = multipleSchedules.current
            ? "/api/schedules"
            : "/api/schedule";

        const response = await put(url, schedule);

        let data: ISchedule | Response;
        try {
            data = await response.json();
        } catch {
            data = response;
        }

        console.log(data);

        if (response.ok) {
            newAlert({
                message: "Schedule edited successfully",
                color: "success",
            });
            getSchedules();
        } else {
            newAlert({
                message: "Error editing schedule",
                color: "danger",
            });
        }
        onSetActiveModal("");
    };

    const editCronScheduleWithRename = async () => {
        const schedule = { ...scheduleClicked };
        const nameWithUnderscores = schedule.name.trim().replace(/\s+/g, "_");
        schedule.name = nameWithUnderscores;

        schedule.trigger = convertCronUserTimezoneToMountain(
            scheduleClicked.trigger
        );

        // If the schedule is a multiple schedule, send it to the multiple schedules endpoint
        // to delete all grouped schedules and replace with one new one
        const url = multipleSchedules.current
            ? "/api/scheduleswithrename"
            : "/api/schedulewithrename";

        const response = await put(url, schedule);

        let data: ISchedule | Response;
        try {
            data = await response.json();
        } catch {
            data = response;
        }

        console.log(data);

        if (response.ok) {
            newAlert({
                message: "Schedule edited successfully",
                color: "success",
            });
            getSchedules();
        } else {
            newAlert({
                message: "Error editing schedule",
                color: "danger",
            });
        }
        onSetActiveModal("");
    };

    const toggleModal = () => {
        onSetActiveModal("");
        onSetScheduleClicked(defaultSchedule);
        setIntegrationInputs(1);
        multipleSchedules.current = null;
        setIsRateInvalid(false);
        setEditError("");
    };

    const onSetIntegrationInputs = useCallback((inputs: number) => {
        setIntegrationInputs(inputs);
    }, []);

    const checkIfNameValid = useCallback(
        (name: string) => {
            // Name must contain at least three characters, and may only consists of
            // digits, letters, hyphens, underscores, spaces or periods. AWS does not allow
            // spaces, commas, etc. in names, but we allow spaces and replace them with underscores
            // on the front end.
            const nameInUse = schedules
                .filter((s) => s.name.trim() !== s.originalName)
                .some(
                    (schedule) =>
                        schedule.name.trim() !== scheduleClicked.originalName &&
                        schedule.name.trim() === name.trim()
                );
            if (nameInUse) {
                setNameValid(false);
                return;
            }

            const valid = validNameRegex.test(name);

            setNameValid(valid);
        },
        [scheduleClicked.originalName, schedules]
    );

    const onSetIsRateInvalid = (isInvalid: boolean) => {
        setIsRateInvalid(isInvalid);
    };

    /** Check if any selected integration no longer exists in the codebase */
    const hasStaleIntegrations = scheduleClicked.integrations.some((integration) => {
        const process = integration.split("/")[0];
        return process
            && process !== "Select Integration"
            && !integrations.some((int) => int.process === process);
    });

    const isDisabled = () => {
        if (hasStaleIntegrations) {
            return true;
        }

        const basicConstraints =
            nameValid === false ||
            scheduleClicked.name.length < 3 ||
            scheduleClicked.integrations[0] === "Select Integration" ||
            scheduleClicked.trigger === "" ||
            scheduleClicked.trigger.startsWith("* ");
        if (basicConstraints) {
            return true;
        }

        const duplicates = schedules.some((s) => {
            // Spaces in form represent the underscores required for an AWS name / names in our data
            const currentNameWithUnderScores = scheduleClicked.name
                .replace(/_+/g, " ")
                .toLowerCase();
            const isSameAsOriginalName =
                scheduleClicked.originalName?.toLowerCase() ===
                currentNameWithUnderScores;
            const isSameAsAnotherName =
                s.name.toLowerCase() === currentNameWithUnderScores;
            return !isSameAsOriginalName && isSameAsAnotherName;
        });
        if (duplicates) {
            return true;
        }

        if (isRateInvalid) {
            return true;
        }

        return false;
    };

    const cronSelectorProps = {
        checkIfNameValid,
        nameValid,
        integrations,
        onSetSchedule: onSetScheduleClicked,
        parent: "edit",
        schedule: scheduleClicked,
        integrationInputs,
        onSetIntegrationInputs,
        isRateInvalid,
        onSetIsRateInvalid,
    };

    return (
        <Draggable handle=".modal-header">
            <Modal isOpen={activeModal === "edit"} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>Edit Schedule</ModalHeader>
                <ModalBody>
                    {" "}
                    <CronSelector {...cronSelectorProps} />
                </ModalBody>
                <ModalFooter
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        paddingRight: "20px",
                    }}
                >
                    <div>
                        {hasStaleIntegrations && (
                            <div className="text-danger">
                                One or more selected integrations no longer exist. Please remove or replace them.
                            </div>
                        )}
                        {editError && <div className="error">{editError}</div>}
                    </div>
                    <Button
                        color="primary"
                        type="submit"
                        form="form"
                        onClick={handleSubmit}
                        disabled={isDisabled()}
                    >
                        Submit
                    </Button>
                </ModalFooter>
            </Modal>
        </Draggable>
    );
}
